import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { getRecentTeams, addRecentTeam } from '../utils/recentTeams';

// react-select theme shared by every searchable dropdown. `compact` is the slim variant used
// inside report cells (lineup slots, pitcher rows).
export function selectStyles({ compact = false, minWidth, height = 38 } = {}) {
  const h = compact ? 22 : height;
  const text = compact ? '0.76rem' : (height > 40 ? '1.05rem' : '0.94rem');
  return {
    control: (base, s) => ({
      ...base,
      minHeight: h,
      height: compact ? h : undefined,
      minWidth,
      fontSize: text,
      background: '#fff',
      borderColor: s.isFocused ? 'var(--ds-navy, #06122d)' : '#d3d8e0',
      borderRadius: compact ? 5 : 9,
      boxShadow: s.isFocused ? '0 0 0 2px rgba(6, 18, 45, 0.12)' : 'none',
      cursor: 'pointer',
      ':hover': { borderColor: 'var(--ds-navy, #06122d)' },
    }),
    valueContainer: (base) => ({ ...base, padding: compact ? '0 5px' : '0 9px', height: compact ? h - 2 : undefined }),
    indicatorsContainer: (base) => ({ ...base, height: compact ? h - 2 : undefined }),
    dropdownIndicator: (base) => ({ ...base, padding: compact ? 1 : 6 }),
    clearIndicator: (base) => ({ ...base, padding: compact ? 1 : 6 }),
    indicatorSeparator: () => ({ display: 'none' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    placeholder: (base) => ({ ...base, color: '#8a93a2' }),
    singleValue: (base) => ({ ...base, color: 'var(--ds-ink, #121826)', fontWeight: 500 }),
    menu: (base) => ({ ...base, zIndex: 60, borderRadius: 9, overflow: 'hidden', boxShadow: '0 10px 24px rgba(6, 18, 45, 0.16)' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, s) => ({
      ...base,
      fontSize: compact ? '0.8rem' : '0.92rem',
      color: s.isSelected ? '#fff' : 'var(--ds-ink, #121826)',
      background: s.isSelected ? 'var(--ds-navy, #06122d)' : s.isFocused ? 'rgba(6, 18, 45, 0.07)' : '#fff',
    }),
  };
}

// Searchable team dropdown. Teams are [{ name, teamId }]; with nothing typed it lists the
// user's recently picked teams first (when there are any), otherwise every team.
export function TeamPicker({ teams, value, onChange, loading = false, placeholder = 'Search team…', minWidth = 210 }) {
  const [typed, setTyped] = useState('');
  const all = useMemo(() => (teams || []).map((t) => ({ label: t.name, value: t.teamId, team: t })), [teams]);
  const options = useMemo(() => {
    if (typed.trim()) return all;
    const recent = getRecentTeams().map((r) => all.find((o) => o.value === r.teamId)).filter(Boolean);
    return recent.length ? recent : all;
  }, [typed, all]);
  const styles = useMemo(() => selectStyles({ minWidth }), [minWidth]);

  return (
    <Select
      options={options}
      value={value ? { label: value.name, value: value.teamId, team: value } : null}
      inputValue={typed}
      onInputChange={setTyped}
      onChange={(opt) => {
        const team = opt ? { name: opt.team.name, teamId: opt.team.teamId } : null;
        if (team) addRecentTeam(team);
        onChange(team);
      }}
      isClearable
      isLoading={loading}
      placeholder={loading ? 'Loading teams…' : placeholder}
      noOptionsMessage={() => (loading ? 'Loading…' : 'No matching teams')}
      maxMenuHeight={260}
      styles={styles}
    />
  );
}

// Searchable player dropdown over /api/players/directory rows ({ fullName, playerId, jersey,
// team }). The menu shows jersey numbers, plus the team when the list spans every team.
// typeToOpen keeps the menu shut until something is typed (for very long lists).
export function PlayerPicker({
  options, value, onChange, loading = false, showTeam = false, placeholder = 'Search player…',
  compact = false, minWidth, typeToOpen = false,
}) {
  const [typed, setTyped] = useState('');
  const styles = useMemo(() => selectStyles({ compact, minWidth }), [compact, minWidth]);
  return (
    <Select
      options={options}
      value={value}
      inputValue={typed}
      onInputChange={setTyped}
      onChange={(o) => onChange(o || null)}
      getOptionLabel={(o) => o.fullName}
      getOptionValue={(o) => `${o.playerId}:${o.fullName}`}
      formatOptionLabel={(o, { context }) => (context === 'value' ? o.fullName
        : `${o.jersey != null ? `#${o.jersey}  ` : ''}${o.fullName}${showTeam && o.team ? ` · ${o.team}` : ''}`)}
      menuIsOpen={typeToOpen && !typed ? false : undefined}
      isClearable
      isLoading={loading}
      placeholder={loading ? 'Loading roster…' : placeholder}
      noOptionsMessage={() => (loading ? 'Loading…' : 'No players found')}
      maxMenuHeight={compact ? 210 : 260}
      styles={styles}
    />
  );
}
